import { useCallback, useEffect, useLayoutEffect, useState } from 'react';

/**
 * Observe an element's rendered width.
 *
 * Use this instead of `useWindowSize` when a layout has to react to the space
 * it actually got rather than to the viewport — e.g. panels that are embedded
 * in both a full-width settings page and a narrower wizard shell.
 *
 * @returns {[(element: any) => void, number]} `[refCallback, width]`
 */
export const useContainerWidth = () => {
  const [node, setNode] = useState(null);
  const ref = useCallback((element) => {
    setNode(element);
  }, []);
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    if (!node) return;
    setWidth(node.getBoundingClientRect().width);
  }, [node]);

  useEffect(() => {
    if (!node || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (rect) setWidth(rect.width);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);

  return [ref, Math.max(0, Math.round(width))];
};
